<script setup>
import { computed } from 'vue';

const props = defineProps({
  ariaLabel: { type: String, default: '' },
  segments: { type: Array, default: () => [] },
});

const CENTER_X = 72;
const CENTER_Y = 60;
const OUTER_RADIUS = 42;
const INNER_RADIUS = 21;

const pointAt = (angle, radius) => {
  const radians = ((angle - 90) * Math.PI) / 180;
  return {
    x: (CENTER_X + radius * Math.cos(radians)).toFixed(4),
    y: (CENTER_Y + radius * Math.sin(radians)).toFixed(4),
  };
};

const fullDonutPath = () =>
  [
    `M ${CENTER_X} ${CENTER_Y - OUTER_RADIUS}`,
    `A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 1 1 ${CENTER_X} ${CENTER_Y + OUTER_RADIUS}`,
    `A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 1 1 ${CENTER_X} ${CENTER_Y - OUTER_RADIUS}`,
    `L ${CENTER_X} ${CENTER_Y - INNER_RADIUS}`,
    `A ${INNER_RADIUS} ${INNER_RADIUS} 0 1 0 ${CENTER_X} ${CENTER_Y + INNER_RADIUS}`,
    `A ${INNER_RADIUS} ${INNER_RADIUS} 0 1 0 ${CENTER_X} ${CENTER_Y - INNER_RADIUS}`,
    'Z',
  ].join(' ');

const segmentPath = (offset, percentage) => {
  if (percentage >= 99.9999) return fullDonutPath();

  const startAngle = offset * 3.6;
  const endAngle = (offset + percentage) * 3.6;
  const outerStart = pointAt(startAngle, OUTER_RADIUS);
  const outerEnd = pointAt(endAngle, OUTER_RADIUS);
  const innerEnd = pointAt(endAngle, INNER_RADIUS);
  const innerStart = pointAt(startAngle, INNER_RADIUS);
  const largeArc = percentage > 50 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${INNER_RADIUS} ${INNER_RADIUS} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
};

const normalizedSegments = computed(() => {
  const total = props.segments.reduce(
    (sum, segment) => sum + Math.max(Number(segment.value) || 0, 0),
    0
  );
  let offset = 0;

  return props.segments
    .map(segment => {
      const value = Math.max(Number(segment.value) || 0, 0);
      const percentage = total ? (value / total) * 100 : 0;
      const current = {
        ...segment,
        offset,
        path: segmentPath(offset, percentage),
        percentage,
        value,
      };
      offset += percentage;
      return current;
    })
    .filter(segment => segment.percentage > 0);
});
</script>

<template>
  <div class="flex h-[300px] items-center justify-center" data-donut-chart>
    <svg
      class="h-[300px] w-full max-w-[300px]"
      viewBox="0 0 120 120"
      role="img"
      :aria-label="ariaLabel || undefined"
    >
      <circle cx="72" cy="60" r="42" class="fill-lais-bg-soft" />
      <path
        v-for="segment in normalizedSegments"
        :key="segment.key"
        :d="segment.path"
        :class="segment.fill"
      />
      <circle cx="72" cy="60" r="21" class="fill-white" />
    </svg>
  </div>
</template>
